import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { IconMoon, IconSun } from './Icons';

type Theme = 'light' | 'dark';
const STORAGE_KEY = 'tp.theme';

const ThemeContext = createContext<{ theme: Theme; toggle: () => void } | null>(null);

function readInitial(): Theme {
  // index.html already stamped data-theme before paint; trust it so the
  // provider and the DOM never disagree on first render.
  const stamped = document.documentElement.dataset.theme;
  return stamped === 'dark' ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(readInitial);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* storage blocked — the theme still applies for this session */
    }
  }, [theme]);

  const toggle = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), []);

  return <ThemeContext.Provider value={{ theme, toggle }}>{children}</ThemeContext.Provider>;
}

export function ThemeToggle() {
  const ctx = useContext(ThemeContext);
  if (!ctx) return null;
  const { theme, toggle } = ctx;
  return (
    <button
      type="button"
      onClick={toggle}
      className="btn btn-ghost size-8 p-0"
      title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
      aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
    >
      {theme === 'dark' ? <IconSun /> : <IconMoon />}
    </button>
  );
}
